import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { JOB_EDUCATION_OPTIONS } from '@edtech-ui/core/constants/recuritment-constants/job-educations.constants';
import { JOB_REQUIRED_SKILLS } from '@edtech-ui/core/constants/recuritment-constants/job-skills.constants';
import { JOB_YEAR_OF_PASSING_OPTIONS } from '@edtech-ui/core/constants/recuritment-constants/job-passing-year.constants';
import { JOB_PERKS_OPTIONS } from '@edtech-ui/core/constants/recuritment-constants/job-pearks-options.constants';
import { JOB_EXPERIENCE_LEVELS } from '@edtech-ui/core/constants/recuritment-constants/job-experience-level.constants';
import { JOB_CATEGORIES } from '@edtech-ui/core/constants/recuritment-constants/job-categories.constants';
import { INDIAN_LOCATIONS } from '@edtech-ui/core/constants/recuritment-constants/indian-locations.constants';
import { JOB_SCREENING_METHOD } from '@edtech-ui/core/constants/recuritment-constants/job-screening-method.constants';
import { RecruitmentJobService } from '@edtech-ui/core/services/recruitment-management/recruitment-job.service';
import { catchError, finalize, of, tap, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { JobDescriptionsValidationUtils } from '@edtech-ui/core/utils/recruitment-utils/job-descriptions';
import { JobRolesResponsibilitiesValidationUtils } from '@edtech-ui/core/utils/recruitment-utils/job-roles-responsibilities';
import { JobRequiredSkillValidationUtils } from '@edtech-ui/core/utils/recruitment-utils/required-skills';
import { CGPAValidationUtils } from '@edtech-ui/core/utils/recruitment-utils/job-cgp';
import { SalaryValidationUtils } from '@edtech-ui/core/utils/recruitment-utils/job-salary';
import { OpeningsValidationUtils } from '@edtech-ui/core/utils/recruitment-utils/job-opening';
import { CompanyPublishPayload } from '@edtech-ui/core/models/company-management/company-approval-status';
import { CompanyOnboardingService } from '@edtech-ui/hiring-workspace/services/company-onboarding.service';

@Component({
	selector: 'app-create-job',
	standalone: true,
	imports: [CommonModule, FormsModule, ReactiveFormsModule],
	templateUrl: './create-job.component.html',
	styleUrls: ['./create-job.component.scss']
})
export class CreateJobComponent implements OnInit {
	currentStep = 1;
	locations: string[] = [''];
	rolesResponsibilities = '';
	newRequiredSkill = '';
	newPreferredSkill = '';
	preferredSkills: string[] = [];
	selectedEducation: string[] = [];
	selectedYears: number[] = [];
	selectedPerks: string[] = [];
	selectedScreeningMethod = JOB_SCREENING_METHOD.ASSESSMENT_AND_AI_INTERVIEW;
	perksOptions = JOB_PERKS_OPTIONS
	yearOfPassingOptions = JOB_YEAR_OF_PASSING_OPTIONS
	educationOptions = JOB_EDUCATION_OPTIONS;
	requiredSkills = JOB_REQUIRED_SKILLS;
	JOB_SCREENING_METHOD = JOB_SCREENING_METHOD;
	categories = JOB_CATEGORIES
	indianLocations = INDIAN_LOCATIONS;
	activeLocationIndex: number | null = null;
	showJobLocationsDropdown = false;
	filteredJobLocations: string[] = [];
	experienceLevels = JOB_EXPERIENCE_LEVELS
	createJobForm: FormGroup;
	companyId = '';
	JobDescriptionsValidationUtils = JobDescriptionsValidationUtils;
	JobRolesResponsibilitiesValidationUtils = JobRolesResponsibilitiesValidationUtils;
	JobRequiredSkillValidationUtils = JobRequiredSkillValidationUtils;
	CGPAValidationUtils = CGPAValidationUtils;
	SalaryValidationUtils = SalaryValidationUtils;
	OpeningsValidationUtils = OpeningsValidationUtils;
	isLoading = false;
	screenType = '';
	jobId = '';
	isDraftLoading = false;
	hiringProcessSteps: string[] = [];
	private subscriptions = new Subscription();

	constructor(
		private jobService: RecruitmentJobService,
		private router: Router,
		private toastr: ToastrService,
		private companyService: CompanyOnboardingService
	) {

		this.createJobForm = new FormGroup({
			job_title: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(100), Validators.pattern(/^(?!\s*$)(?!\d+$)(?![&\-/() ]+$)[A-Za-z0-9&\-\/() ]+$/)]),
			job_role_category: new FormControl('', [Validators.required]),
			custom_job_role_category: new FormControl(''),
			job_type: new FormControl('Full-Time'),
			work_mode: new FormControl('Onsite'),
			locations: new FormArray([new FormControl('', [Validators.required, singleLocationValidator])]),
			description: new FormControl('', [Validators.required, JobDescriptionsValidationUtils.jobDescriptionValidator()]),
			roles_and_responsibilities: new FormControl('', [
				Validators.required,
				JobRolesResponsibilitiesValidationUtils.rolesResponsibilitiesValidator()
			]),
			required_skills: new FormArray([], [JobRequiredSkillValidationUtils.requiredSkillsValidator()]),
			preferred_skills: new FormArray([]),
			education: new FormArray([]),
			min_cgpa: new FormControl('', [CGPAValidationUtils.cgpaValidator()]),
			experience_level: new FormControl(''),
			year_of_passing: new FormControl([]),
			salary_min: new FormControl('', [SalaryValidationUtils.salaryValidator()]),
			salary_max: new FormControl('', [SalaryValidationUtils.salaryValidator()]),
			currency: new FormControl('₹'),
			payment_frequency: new FormControl('LPA'),
			expected_full_time_salary: new FormControl(''),
			expected_full_time_salary_currency: new FormControl('₹'),
			expected_full_time_salary_frequency: new FormControl('LPA'),
			number_of_openings: new FormControl('', [OpeningsValidationUtils.openingsValidator()]),
			perks_and_benefits: new FormArray([]),
			application_deadline: new FormControl('', [futureDateValidator]),
			newRequiredSkill: new FormControl('', [singleSkillValidator]),
			duration: new FormControl(''),
			duration_unit: new FormControl('Months'),
			is_visible_on_student_dashboard: new FormControl(false),
			hiring_process: new FormControl([]),
			default_passing_score: new FormControl(40, [Validators.required, Validators.min(1), Validators.max(100)])
		});
	}

	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.setScreeningMethod(this.JOB_SCREENING_METHOD.ASSESSMENT_AND_AI_INTERVIEW);
		
		this.companyService.getCompanyProfile().subscribe(res => {
			if (res.Success && res.Data) {
				const defaultScore = res.Data.default_passing_score || 40;
				this.createJobForm.get('default_passing_score')?.setValue(defaultScore);
			}
		});

		this.subscriptions.add(
			this.createJobForm.get('job_role_category')?.valueChanges.subscribe(value => {
				const customCtrl = this.createJobForm.get('custom_job_role_category');
				if (value === 'Other') {
					customCtrl?.setValidators([
						Validators.required,
						Validators.minLength(2),
						Validators.maxLength(30),
						Validators.pattern(/^[A-Za-z\s]+$/)
					]);
				} else {
					customCtrl?.clearValidators();
					customCtrl?.setValue('');
				}
				customCtrl?.updateValueAndValidity();
			})
		);

		this.subscriptions.add(
			this.createJobForm.get('job_type')?.valueChanges.subscribe(jobType => {
				const freqCtrl = this.createJobForm.get('payment_frequency');
				if (jobType === 'Internship/Training') {
					freqCtrl?.setValue('Per Month');
				} else {
					freqCtrl?.setValue('LPA');
				}
			})
		);
	}


	get todayDate(): string {
		const today = new Date();
		const year = today.getFullYear();
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const day = String(today.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	nextStep(): void {
		if (this.currentStep === 1) {
			this.createJobForm.markAllAsTouched();
			if (this.createJobForm.invalid) {
				return;
			}
			this.currentStep = 2;
		}
	}

	prevStep(): void {
		if (this.currentStep === 2) {
			this.currentStep = 1;
		}
	}

	getLocations(): string {
		const loc = this.createJobForm.value.locations || [];
		return loc.filter((l: string) => l?.trim()).join(', ') || '—';
	}


	submitJob(): void {
		const formValue = this.createJobForm.value;
		let finalJobRoleCategory = formValue.job_role_category;
		if (finalJobRoleCategory === 'Other') {
			finalJobRoleCategory = formValue.custom_job_role_category?.trim();
		}

		const payload = {
			...formValue,
			job_role_category: finalJobRoleCategory,
			locations: (formValue.locations || []).map((l: string) => l?.trim()).filter((l: string) => l && l.length > 0),
			salary_min: formValue.salary_min ? (!isNaN(Number(formValue.salary_min)) ? Number(formValue.salary_min) : formValue.salary_min) : null,
			salary_max: formValue.salary_max ? (!isNaN(Number(formValue.salary_max)) ? Number(formValue.salary_max) : formValue.salary_max) : null,
			expected_full_time_salary: formValue.expected_full_time_salary ? (!isNaN(Number(formValue.expected_full_time_salary)) ? Number(formValue.expected_full_time_salary) : formValue.expected_full_time_salary) : null,
			number_of_openings: formValue.number_of_openings ? Number(formValue.number_of_openings) : null,
			education: this.selectedEducation,
			year_of_passing: this.selectedYears,
			perks_and_benefits: this.selectedPerks,
			required_skills: this.requiredSkillsList,
			preferred_skills: this.preferredSkills,
			screening_method: this.selectedScreeningMethod.toLowerCase(),
			is_visible_on_student_dashboard: formValue.is_visible_on_student_dashboard
		}
		this.isLoading = true;

		this.jobService.postCreateJob(payload, this.companyId).pipe(
			tap(response => {
				if (response.Success) {
					this.screenType = response.Data.screening_method;
					this.jobId = response.Data.job_id;
					this.postCompanyPublish();
				} else {
					this.toastr.error(response.Message);
				}
			}),
			finalize(() => {
				this.isLoading = false;
			}),
			catchError(() => {
				this.toastr.error('Something went worng.Please try again');
				return of(null)
			})
		).subscribe()
	}


	submitDraftJob(): void {
		const formValue = this.createJobForm.value;
		let finalJobRoleCategory = formValue.job_role_category;
		if (finalJobRoleCategory === 'Other') {
			finalJobRoleCategory = formValue.custom_job_role_category?.trim();
		}

		const payload = {
			...formValue,
			job_role_category: finalJobRoleCategory,
			locations: (formValue.locations || []).map((l: string) => l?.trim()).filter((l: string) => l && l.length > 0),
			salary_min: formValue.salary_min ? (!isNaN(Number(formValue.salary_min)) ? Number(formValue.salary_min) : formValue.salary_min) : null,
			salary_max: formValue.salary_max ? (!isNaN(Number(formValue.salary_max)) ? Number(formValue.salary_max) : formValue.salary_max) : null,
			expected_full_time_salary: formValue.expected_full_time_salary ? (!isNaN(Number(formValue.expected_full_time_salary)) ? Number(formValue.expected_full_time_salary) : formValue.expected_full_time_salary) : null,
			number_of_openings: formValue.number_of_openings ? Number(formValue.number_of_openings) : null,
			education: this.selectedEducation,
			year_of_passing: this.selectedYears,
			perks_and_benefits: this.selectedPerks,
			required_skills: this.requiredSkillsList,
			preferred_skills: this.preferredSkills,
			screening_method: this.selectedScreeningMethod.toLowerCase(),
			is_visible_on_student_dashboard: formValue.is_visible_on_student_dashboard
		}
		this.isDraftLoading = true;

		this.jobService.postCreateJob(payload, this.companyId).pipe(
			tap(response => {
				if (response.Success) {
					this.screenType = response.Data.screening_method;
					this.jobId = response.Data.job_id;
					this.toastr.success('Your job has been successfully created and is now saved as a draft.');
					this.navigateToJobsTab();
				} else {
					this.toastr.error(response.Message);
				}
			}),
			finalize(() => {
				this.isDraftLoading = false;
			}),
			catchError(() => {
				this.toastr.error('Something went worng.Please try again');
				return of(null)
			})
		).subscribe()
	}


	setScreeningMethod(method: string): void {
		this.selectedScreeningMethod = method;

		switch (method) {
			case this.JOB_SCREENING_METHOD.ASSESSMENT_ONLY:
				this.hiringProcessSteps = [
					'AI-Proctored Assessment',
					'Recruiter Review & Final Decision'
				];
				break;

			case this.JOB_SCREENING_METHOD.AI_INTERVIEW_ONLY:
				this.hiringProcessSteps = [
					'AI-Proctored Video Interview',
					'Recruiter Review & Final Decision'
				];
				break;

			case this.JOB_SCREENING_METHOD.ASSESSMENT_AND_AI_INTERVIEW:
				this.hiringProcessSteps = [
					'AI-Proctored Assessment',
					'AI-Proctored Video Interview',
					'Recruiter Review & Final Decision'
				];
				break;
		}
		this.createJobForm.get('hiring_process')?.setValue(this.hiringProcessSteps);
	}


	navigateToJobsTab(): void {
		this.router.navigate(['/recruiter-dashboard/jobs']);
	}

	onCustomCategoryInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		let value = input.value;

		value = value.replace(/[^A-Za-z\s]/g, '');

		const control = this.createJobForm.get('custom_job_role_category');
		if (control) {
			control.setValue(value, { emitEvent: false });
			control.markAsTouched();
		}
	}


	toggleEducation(edu: string): void {
		const i = this.selectedEducation.indexOf(edu);
		i > -1 ? this.selectedEducation.splice(i, 1) : this.selectedEducation.push(edu);
	}

	toggleYear(year: number): void {
		const i = this.selectedYears.indexOf(year);
		i > -1 ? this.selectedYears.splice(i, 1) : this.selectedYears.push(year);
	}

	togglePerk(perk: string): void {
		const i = this.selectedPerks.indexOf(perk);
		i > -1 ? this.selectedPerks.splice(i, 1) : this.selectedPerks.push(perk);
	}

	addRequiredSkill(): void {
		const ctrl = this.createJobForm.get('newRequiredSkill');
		if (ctrl?.invalid) {
			ctrl.markAsTouched();
			return;
		}
		const skill = ctrl?.value?.trim();

		if (!skill) return;

		const skillsFA = this.createJobForm.get('required_skills') as FormArray;
		const exists = skillsFA.value.includes(skill);

		if (exists) {
			this.toastr.warning('Skill already added');
			return;
		}

		skillsFA.push(new FormControl(skill));
		ctrl?.setValue('');
		skillsFA.updateValueAndValidity();
		skillsFA.markAsTouched();
	}


	removeRequiredSkill(skill: string): void {
		const skillsFA = this.createJobForm.get('required_skills') as FormArray;

		const index = skillsFA.controls.findIndex(
			c => c.value === skill
		);

		if (index > -1) {
			skillsFA.removeAt(index);
			skillsFA.updateValueAndValidity();
			skillsFA.markAsTouched();
		}
	}

	get requiredSkillsList(): string[] {
		return this.createJobForm.get('required_skills')?.value || [];
	}


	addPreferredSkill(): void {
		const skill = this.newPreferredSkill.trim();
		if (skill && !this.preferredSkills.includes(skill)) {
			this.preferredSkills.push(skill);
			this.newPreferredSkill = '';
		}
	}

	removePreferredSkill(skill: string): void {
		this.preferredSkills = this.preferredSkills.filter(s => s !== skill);
	}

	trackByIndex(index: number, obj: any): any {
		return index;
	}

	get locationsDisplay(): string {
		const arr = this.locationsFA?.value || [];

		return arr
			.filter((l: string) => l && l.trim().length > 0)
			.join(', ') || '—';
	}

	get locationsFA(): FormArray {
		return this.createJobForm.get('locations') as FormArray;
	}

	onJobLocationInput(event: Event, index: number): void {
		const value = (event.target as HTMLInputElement).value;
		this.activeLocationIndex = index;
		if (value && value.trim().length >= 1) {
			const query = value.toLowerCase();
			this.filteredJobLocations = this.indianLocations.filter(loc =>
				loc.toLowerCase().includes(query)
			);
			this.showJobLocationsDropdown = true;
		} else {
			this.filteredJobLocations = [];
			this.showJobLocationsDropdown = false;
		}
	}

	onJobLocationFocus(event: Event, index: number): void {
		const value = (event.target as HTMLInputElement).value;
		this.activeLocationIndex = index;
		if (value && value.trim().length >= 1) {
			const query = value.toLowerCase();
			this.filteredJobLocations = this.indianLocations.filter(loc =>
				loc.toLowerCase().includes(query)
			);
			this.showJobLocationsDropdown = true;
		} else {
			this.filteredJobLocations = [];
			this.showJobLocationsDropdown = false;
		}
	}

	onJobLocationBlur(index: number): void {
		if (this.activeLocationIndex === index) {
			this.showJobLocationsDropdown = false;
		}
	}

	selectJobLocation(loc: string, index: number): void {
		const control = this.locationsFA.at(index);
		if (control) {
			control.setValue(loc);
		}
		this.showJobLocationsDropdown = false;
		this.activeLocationIndex = null;
	}

	addLocation(): void {
		if (this.locationsFA.invalid) {
			this.locationsFA.markAllAsTouched();
			this.toastr.error('Please resolve validation errors first.');
			return;
		}
		this.locationsFA.push(new FormControl('', [Validators.required, singleLocationValidator]));
	}

	removeLocation(index: number): void {
		if (this.locationsFA.length > 1) {
			this.locationsFA.removeAt(index);
		} else {
			this.locationsFA.at(0).setValue('');
		}
	}

	onDurationInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		let value = input.value.replace(/[^0-9]/g, '');
		if (value.startsWith('0')) {
			value = value.replace(/^0+/, '');
		}

		const control = this.createJobForm.get('duration');
		if (control) {
			control.setValue(value ? parseInt(value, 10) : null, { emitEvent: false });
		}
	}


	onCgpaInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		let value = input.value;
		value = value.replace(/[^0-9.]/g, '');

		const parts = value.split('.');
		if (parts.length > 2) {
			value = parts[0] + '.' + parts.slice(1).join('');
		}

		if (parts.length === 2 && parts[1].length > 2) {
			value = parts[0] + '.' + parts[1].substring(0, 2);
		}
		const num = parseFloat(value);
		if (!isNaN(num) && num > 10) {
			value = '10';
		}

		const control = this.createJobForm.get('min_cgpa');
		if (control) {
			control.setValue(value, { emitEvent: false });
			control.markAsTouched();
		}
	}

	onSalaryInput(event: Event, controlName: string): void {
		const input = event.target as HTMLInputElement;
		let value = input.value.replace(/[^0-9.]/g, '');
		const parts = value.split('.');
		if (parts.length > 2) {
			value = parts[0] + '.' + parts.slice(1).join('');
		}

		const control = this.createJobForm.get(controlName);
		if (control) {
			control.setValue(value, { emitEvent: false });
			control.markAsTouched();
		}

		if (controlName === 'salary_min') {
			const maxCtrl = this.createJobForm.get('salary_max');
			if (maxCtrl) {
				maxCtrl.setValue(value, { emitEvent: false });
				maxCtrl.markAsTouched();
			}
		}
		this.validateSalaryRange();
	}

	validateSalaryRange(): void {
		const minCtrl = this.createJobForm.get('salary_min');
		const maxCtrl = this.createJobForm.get('salary_max');

		if (!minCtrl || !maxCtrl) return;

		const minVal = parseFloat(minCtrl.value);
		const maxVal = parseFloat(maxCtrl.value);

		if (minCtrl.errors) {
			const { minExceedsMax, ...rest } = minCtrl.errors;
			minCtrl.setErrors(Object.keys(rest).length ? rest : null);
		}

		if (!isNaN(minVal) && !isNaN(maxVal) && minVal > maxVal) {
			minCtrl.setErrors({ ...(minCtrl.errors || {}), minExceedsMax: true });
		}
	}

	onOpeningsInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		let value = input.value.replace(/[^0-9]/g, '');
		value = value.replace(/^0+/, '');
		const num = parseInt(value, 10);
		if (!isNaN(num) && num > 9999) {
			value = '9999';
		}

		const control = this.createJobForm.get('number_of_openings');
		if (control) {
			control.setValue(value, { emitEvent: false });
			control.markAsTouched();
		}
	}

	getScreeningMethodLabel(method: string): string {
		if (method === this.JOB_SCREENING_METHOD.ASSESSMENT_ONLY) {
			return 'Assessment only';
		} else if (method === this.JOB_SCREENING_METHOD.AI_INTERVIEW_ONLY) {
			return 'AI Interview only';
		} else if (method === this.JOB_SCREENING_METHOD.ASSESSMENT_AND_AI_INTERVIEW) {
			return 'Assessment + AI Interview';
		}
		return method;
	}


	postCompanyPublish(): void {

		const payload: CompanyPublishPayload = {
			screening_method: this.screenType
		};

		this.jobService.postCompanyPublish(payload, this.jobId, this.companyId).pipe(
			tap((response) => {
				if (response.Success) {
					this.currentStep = 3;
				} else {
					this.toastr.error(response.Message);
				}
			}),
			catchError(() => {
				this.toastr.error('Publish API failed');
				return of(null);
			})

		).subscribe();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	formatReviewSalary(val: any, currency: string = '₹', freq: string = 'LPA'): string {
		if (val === null || val === undefined || val === '') return '—';
		const num = Number(val);
		if (isNaN(num)) return String(val);
		if (freq === 'LPA') {
			const lpaVal = num >= 1000 ? num / 100000 : num;
			const formatted = Number(lpaVal.toFixed(2)).toString();
			return `${currency} ${formatted} LPA`;
		}
		return `${currency} ${num} ${freq}`;
	}
}

export function singleLocationValidator(control: AbstractControl): ValidationErrors | null {
	const value = control.value?.trim();
	if (!value) return null;

	const matchedLocation = INDIAN_LOCATIONS.find(loc => loc.toLowerCase() === value.toLowerCase());
	if (!matchedLocation) {
		return { invalidLocation: true };
	}

	const parts = matchedLocation.split(',').map((p: string) => p.trim());
	const partsCount = parts.filter((p: string) => p.length > 0).length;

	const commonRegions = [
		'india', 'usa', 'united states', 'uk', 'united kingdom', 'canada', 'germany', 'australia', 'singapore', 'uae',
		'maharashtra', 'karnataka', 'tamil nadu', 'tamilnadu', 'telangana', 'andhra pradesh', 'delhi', 'gujarat', 'uttar pradesh', 'up', 'haryana', 'punjab', 'kerala', 'rajasthan', 'madhya pradesh', 'bihar', 'west bengal', 'odisha',
		'himachal pradesh', 'chhattisgarh', 'jharkhand', 'chandigarh', 'uttarakhand', 'jammu & kashmir', 'jammu and kashmir', 'goa', 'assam', 'arunachal pradesh', 'manipur', 'meghalaya', 'mizoram', 'nagaland', 'sikkim', 'tripura', 'union territory', 'ut'
	];

	let isMultiple = false;
	if (partsCount > 2) {
		isMultiple = true;
	} else if (partsCount === 2) {
		const secondPart = parts[1].toLowerCase();
		if (!commonRegions.includes(secondPart)) {
			isMultiple = true;
		}
	}

	if (isMultiple) {
		return { multipleLocations: true };
	}
	return null;
}

export function singleSkillValidator(control: AbstractControl): ValidationErrors | null {
	const value = control.value?.trim();
	if (!value) return null;

	if (value.includes(',')) {
		return { multipleSkills: true };
	}
	return null;
}

export function futureDateValidator(control: AbstractControl): ValidationErrors | null {
	const value = control.value;
	if (!value) return null;

	const selectedDate = new Date(value);
	selectedDate.setHours(0, 0, 0, 0);

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	if (selectedDate < today) {
		return { pastDate: true };
	}
	return null;



}